import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {Icon} from '@forecast-it/design-system';
import {useAgentDrawerContext} from '../contexts/AgentDrawerContext';

const DRAWER_WIDTH = 480;
const ZENDESK_IFRAME_SELECTOR = 'iframe[title="Messaging window"]';
const ZENDESK_INSET_FALLBACK = 'auto 16px 75px auto';
const ZENDESK_STYLE_TAG_ID = 'agent-drawer-zendesk-offset';

const ChatButton = styled.button`
	z-index: 1000000;
	position: absolute;
	bottom: 24px;
	right: ${({drawerOpen}) => (drawerOpen ? DRAWER_WIDTH + 24 : 24)}px;
	transition: right 250ms cubic-bezier(0.4, 0, 0.2, 1);

	width: 44px;
	height: 44px;

	padding: 0;
	border: none;

	display: flex;
	align-items: center;
	justify-content: center;
`;

const ChatIconCircle = styled.div`
	border-radius: 50%;
	width: 40px;
	height: 40px;

	border: 2px solid #650ed8;
	background: #ffffff;

	display: flex;
	align-items: center;
	justify-content: center;
`;

const ChatIcon = () => {
	const [opened, setOpened] = useState(false);
	const {isOpen: drawerOpen} = useAgentDrawerContext();
	// Zendesk's default right offset, captured once from the live iframe
	const defaultRightPxRef = useRef(null);

	// Watch for Zendesk's iframe and capture its default right offset once
	useEffect(() => {
		function capture() {
			if (defaultRightPxRef.current !== null) return;
			const iframe = document.querySelector(ZENDESK_IFRAME_SELECTOR);
			if (!iframe) return;
			const parts = (iframe.style.inset || ZENDESK_INSET_FALLBACK).split(' ');
			defaultRightPxRef.current = parseInt(parts[1] ?? '16', 10);
		}
		capture();
		const domObserver = new MutationObserver(capture);
		domObserver.observe(document.body, {childList: true, subtree: true});
		return () => domObserver.disconnect();
	}, []);

	// Inject a stylesheet override when the drawer is open.
	//
	// We use !important in a <style> tag rather than manipulating inline styles directly.
	// A stylesheet !important rule beats Zendesk's inline inset (which has no !important),
	// and applies to any iframe Zendesk creates after the drawer opens — solving the race
	// where the iframe doesn't exist yet. Removing the tag restores Zendesk's own positioning.
	useEffect(() => {
		if (drawerOpen) {
			const defaultRight = defaultRightPxRef.current ?? 16;
			let el = document.getElementById(ZENDESK_STYLE_TAG_ID);
			if (!el) {
				el = document.createElement('style');
				el.id = ZENDESK_STYLE_TAG_ID;
				document.head.appendChild(el);
			}
			el.textContent = `${ZENDESK_IFRAME_SELECTOR} { right: ${DRAWER_WIDTH + defaultRight}px !important; }`;
		} else {
			document.getElementById(ZENDESK_STYLE_TAG_ID)?.remove();
		}
		return () => document.getElementById(ZENDESK_STYLE_TAG_ID)?.remove();
	}, [drawerOpen]);

	function toggleChatWindow() {
		if (process.env.CIRCLE_BRANCH !== 'production') return;

		if (opened) {
			window.zE('messenger', 'close');
			setOpened(false);
		} else {
			window.zE('messenger', 'open');
			setOpened(true);
		}
	}

	return (
		<ChatButton onClick={toggleChatWindow} drawerOpen={drawerOpen}>
			<ChatIconCircle>
				<Icon icon="feedback" color="primary" size="l" />
			</ChatIconCircle>
		</ChatButton>
	);
};

export default ChatIcon;
